const chapter = {
  id: 'transistors-logic-gates',
  slug: 'transistors-logic-gates',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Transistors & Logic Gates',
        hindi: 'ट्रांजिस्टर और तर्क द्वार',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Electronics', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'bjt-structure',
      title: {
        english: 'Bipolar Junction Transistor (BJT) — Structure',
        hindi: 'द्विध्रुवी जंक्शन ट्रांजिस्टर (BJT) — संरचना',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Three regions: **Emitter (E)**, **Base (B)**, **Collector (C)**. Two junctions: E–B and B–C.',
        hindi: 'तीन क्षेत्र: **एमिटर (ई)**, **बेस (बी)**, **कलेक्टर (सी)**। दो जंक्शन: ई-बी और बी-सी।',
            },
            {
              english: 'Two types: **n-p-n** (more common, electrons are majority carriers in E/C) and **p-n-p**.',
        hindi: 'दो प्रकार: **n-p-n** (अधिक सामान्य, E/C में इलेक्ट्रॉन बहुसंख्यक वाहक होते हैं) और **p-n-p**।',
            },
            {
              english: 'Emitter is **heavily doped** & moderate-sized; base is **thin & lightly doped**; collector is **moderately doped & largest** (handles heat).',
        hindi: 'एमिटर **भारी डोप्ड** और मध्यम आकार का है; आधार **पतला और हल्का डोप्ड** है; कलेक्टर **मध्यम रूप से डोप किया गया और सबसे बड़ा** है (गर्मी को संभालता है)।',
            },
            {
              english: 'Active mode: E–B forward biased, B–C reverse biased. Cut-off: both junctions reverse. Saturation: both forward.',
        hindi: 'सक्रिय मोड: ई-बी फॉरवर्ड बायस्ड, बी-सी रिवर्स बायस्ड। कट-ऑफ: दोनों जंक्शन विपरीत हैं। संतृप्ति: दोनों आगे.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Current relations',
        hindi: 'वर्तमान संबंध',
          },
          statement: {
            english: '$I_E = I_B + I_C$. Common-base **current gain** $\\alpha = I_C/I_E$ (~0.95–0.99). Common-emitter **current gain** $\\beta = I_C/I_B$. Relation: $\\beta = \\alpha/(1 - \\alpha)$, $\\alpha = \\beta/(\\beta + 1)$.',
        hindi: '$I_E = I_B + I_C$. सामान्य-आधार **वर्तमान लाभ** $\\alpha = I_C/I_E$ (~0.95–0.99)। सामान्य-उत्सर्जक **वर्तमान लाभ** $\\beta = I_C/I_B$। संबंध: $\\beta = \\alpha/(1 - \\alpha)$, $\\alpha = \\beta/(\\beta + 1)$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Most carriers from emitter cross the thin base into the collector — only a tiny fraction recombine in base ($I_B$ small).',
        hindi: 'उत्सर्जक से अधिकांश वाहक पतले आधार को संग्राहक में पार करते हैं - आधार में केवल एक छोटा सा अंश पुनः संयोजित होता है ($I_B$ छोटा)।',
            },
            {
              english: 'Voltage gain: $A_v = \\beta \\cdot R_L/r_i$ where $r_i$ is input resistance.',
        hindi: 'वोल्टेज लाभ: $A_v = \\beta \\cdot R_L/r_i$ जहां $r_i$ इनपुट प्रतिरोध है।',
            },
            {
              english: 'Power gain: $A_p = \\beta \\cdot A_v$.',
        hindi: 'पावर गेन: $A_p = \\beta \\cdot A_v$.',
            },
          ],
        },
      ],
    },
    {
      id: 'transistor-action',
      title: {
        english: 'Transistor as Amplifier & Switch',
        hindi: 'प्रवर्धक और स्विच के रूप में ट्रांजिस्टर',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'In **CE amplifier**, small AC signal at base produces a much larger AC signal at collector across $R_L$. Output is **180° out of phase** with input. DC operating point (Q-point) chosen in middle of active region for distortion-free amplification.',
        hindi: '**CE एम्पलीफायर** में, बेस पर छोटा AC सिग्नल $R_L$ के पार कलेक्टर पर बहुत बड़ा AC सिग्नल उत्पन्न करता है। इनपुट के साथ आउटपुट **180° फ़ेज़ से बाहर** है। विरूपण-मुक्त प्रवर्धन के लिए सक्रिय क्षेत्र के मध्य में डीसी ऑपरेटिंग पॉइंट (क्यू-पॉइंट) चुना गया है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Voltage gain $A_v = -\\beta \\cdot R_L/r_i$. Negative sign denotes phase inversion.',
        hindi: 'वोल्टेज लाभ $A_v = -\\beta \\cdot R_L/r_i$। ऋणात्मक चिन्ह चरण व्युत्क्रम को दर्शाता है।',
            },
            {
              english: 'As **switch**: in cut-off (open) when $V_{BE} < V_b$; in saturation (closed, $V_{CE} \\approx 0$) when base current is large.',
        hindi: '**स्विच** के रूप में: कट-ऑफ में (खुला) जब $V_{BE} < V_b$; संतृप्ति में (बंद, $V_{CE} \\approx 0$) जब आधार धारा बड़ी हो।',
            },
            {
              english: 'Cut-off ↔ logic 0; saturation ↔ logic 1. Forms basis of digital logic.',
        hindi: 'कट-ऑफ ↔ तर्क 0; संतृप्ति ↔ तर्क 1. डिजिटल तर्क का आधार बनता है।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'In a transistor, $\\beta = 100$ and $I_B = 20\\,\\mu$A. Find $I_E$.',
        hindi: 'एक ट्रांजिस्टर में, $\\beta = 100$ और $I_B = 20\\,\\mu$A। $I_E$ खोजें।',
          },
          solution: {
            english: '$I_C = \\beta I_B = 2$ mA. $I_E = I_B + I_C = 0.02 + 2 = 2.02$ mA.',
        hindi: '$I_C = \\beta I_B = 2$ एमए. $I_E = I_B + I_C = 0.02 + 2 = 2.02$ एमए.',
          },
        },
      ],
    },
    {
      id: 'logic-gates',
      title: {
        english: 'Logic Gates',
        hindi: 'तर्क द्वार',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Inputs/outputs are binary (0 = LOW, 1 = HIGH). Combinations of gates implement Boolean algebra and arithmetic.',
        hindi: 'Inputs/outputs बाइनरी हैं (0 = कम, 1 = उच्च)। गेट्स का संयोजन बूलियन बीजगणित और अंकगणित को लागू करता है।',
          },
        },
        {
          type: 'table',
          headers: ['Gate', 'Symbol', 'Boolean', 'Truth (A,B → Y)'],
          rows: [
            ['NOT', '$\\bar A$', '$Y = \\bar A$', '$0\\to 1,\\ 1\\to 0$'],
            ['AND', 'A·B', '$Y = A\\cdot B$', '$00\\to 0,\\ 01\\to 0,\\ 10\\to 0,\\ 11\\to 1$'],
            ['OR', 'A+B', '$Y = A + B$', '$00\\to 0,\\ 01\\to 1,\\ 10\\to 1,\\ 11\\to 1$'],
            ['NAND', '$\\overline{A\\cdot B}$', '$Y = \\overline{A\\cdot B}$', '$00\\to 1,\\ 01\\to 1,\\ 10\\to 1,\\ 11\\to 0$'],
            ['NOR', '$\\overline{A+B}$', '$Y = \\overline{A+B}$', '$00\\to 1,\\ 01\\to 0,\\ 10\\to 0,\\ 11\\to 0$'],
            ['XOR', '$A\\oplus B$', '$Y = A\\bar B + \\bar A B$', '$00\\to 0,\\ 01\\to 1,\\ 10\\to 1,\\ 11\\to 0$'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: '**NAND** and **NOR** are **universal gates** — any logic circuit can be built from NAND gates alone (or NOR gates alone). Useful in IC design.',
        hindi: '**NAND** और **NOR** **यूनिवर्सल गेट्स** हैं - कोई भी लॉजिक सर्किट अकेले NAND गेट्स (या अकेले NOR गेट्स) से बनाया जा सकता है। आईसी डिजाइन में उपयोगी.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'NOT from NAND: tie both inputs together.',
        hindi: 'NAND से नहीं: दोनों इनपुट को एक साथ जोड़ें।',
            },
            {
              english: 'AND from NAND: NAND followed by NOT (= NAND with shorted inputs).',
        hindi: 'और NAND से: NAND के बाद NOT (= संक्षिप्त इनपुट के साथ NAND)।',
            },
            {
              english: 'OR from NAND: invert each input first, then NAND (De Morgan).',
        hindi: 'या NAND से: पहले प्रत्येक इनपुट को उल्टा करें, फिर NAND (डी मॉर्गन)।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'For inputs A = 1, B = 0, find outputs of AND, OR, NAND, XOR.',
        hindi: 'इनपुट A = 1, B = 0 के लिए, AND, OR, NAND, XOR के आउटपुट ढूंढें।',
          },
          solution: {
            english: 'AND = 0, OR = 1, NAND = 1, XOR = 1.',
        hindi: 'और = 0, या = 1, नंद = 1, एक्सओआर = 1।',
          },
        },
      ],
    },
    {
      id: 'integrated-circuits',
      title: {
        english: 'Integrated Circuits — A Note',
        hindi: 'एकीकृत परिपथ — एक टिप्पणी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'IC: many components (transistors, diodes, resistors) on a single Si chip.',
        hindi: 'IC: एक ही Si चिप पर कई घटक (ट्रांजिस्टर, डायोड, प्रतिरोधक)। जटिलता के आधार पर वर्गीकरण: SSI (~10), MSI (~100), LSI (~), VLSI (~), ULSI ()।',
            },
            {
              english: 'Classification by complexity: SSI (~10), MSI (~100), LSI (~$10^4$), VLSI (~$10^6$), ULSI ($> 10^7$).',
        hindi: 'जटिलता के आधार पर वर्गीकरण: एसएसआई (~10), एमएसआई (~100), एलएसआई (~$10^4$), वीएलएसआई (~$10^6$), यूएलएसआई ($> 10^7$)।',
            },
            {
              english: 'Microprocessors and memory chips are VLSI/ULSI examples.',
        hindi: 'माइक्रोप्रोसेसर और मेमोरी चिप्स VLSI/ULSI उदाहरण हैं।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'I_E = I_B + I_C',
      desc: {
        english: 'Transistor currents',
        hindi: 'ट्रांजिस्टर धाराएँ',
      },
    },
    {
      latex: '\\alpha = I_C/I_E',
      desc: {
        english: 'CB current gain',
        hindi: 'सीबी वर्तमान लाभ',
      },
    },
    {
      latex: '\\beta = I_C/I_B',
      desc: {
        english: 'CE current gain',
        hindi: 'CE वर्तमान लाभ',
      },
    },
    {
      latex: '\\beta = \\alpha/(1 - \\alpha)',
      desc: {
        english: 'Relation',
        hindi: 'संबंध',
      },
    },
    {
      latex: 'A_v = -\\beta R_L/r_i',
      desc: {
        english: 'CE voltage gain',
        hindi: 'CE वोल्टेज लाभ',
      },
    },
    {
      latex: 'Y_{NAND} = \\overline{A\\cdot B}',
      desc: {
        english: 'NAND',
        hindi: 'नंद',
      },
    },
    {
      latex: 'Y_{XOR} = A\\bar B + \\bar A B',
      desc: {
        english: 'XOR',
        hindi: 'XOR',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'BJT',
      problem: {
        english: 'If $\\alpha = 0.98$, find $\\beta$.',
        hindi: 'यदि $\\alpha = 0.98$, तो $\\beta$ ज्ञात करें।',
      },
      answer: {
        english: '$49$',
        hindi: '$49$',
      },
      solution: {
        english: '$\\beta = 0.98/0.02 = 49$.',
        hindi: '$\\beta = 0.98/0.02 = 49$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Logic',
      problem: {
        english: 'Boolean expression $Y = \\overline{A + B}$ corresponds to which gate?',
        hindi: 'बूलियन अभिव्यक्ति $Y = \\overline{A + B}$ किस गेट से मेल खाती है?',
      },
      answer: {
        english: 'NOR',
        hindi: 'न ही',
      },
      solution: {
        english: 'Negation of OR.',
        hindi: 'OR का निषेध।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Universal',
      problem: {
        english: 'Which gate is universal: AND, OR, XOR, NAND?',
        hindi: 'कौन सा गेट सार्वभौमिक है: AND, OR, XOR, NAND?',
      },
      answer: {
        english: 'NAND',
        hindi: 'नंद',
      },
      solution: {
        english: 'NAND and NOR are universal.',
        hindi: 'NAND और NOR सार्वभौमिक हैं।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Amplifier',
      problem: {
        english: 'CE amplifier output and input are out of phase by:',
        hindi: 'CE एम्पलीफायर आउटपुट और इनपुट चरण से बाहर हैं:',
      },
      answer: {
        english: '$180°$',
        hindi: '$180°$',
      },
      solution: {
        english: 'Inversion across $R_L$ when $I_C$ rises.',
        hindi: '$I_C$ बढ़ने पर $R_L$ में व्युत्क्रमण।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Truth table',
      problem: {
        english: 'For inputs A = 1, B = 1 to an XOR gate, output is:',
        hindi: 'XOR गेट के इनपुट A = 1, B = 1 के लिए, आउटपुट है:',
      },
      answer: {
        english: '$0$',
        hindi: '$0$',
      },
      solution: {
        english: 'XOR is 1 only when inputs differ.',
        hindi: 'XOR केवल तभी 1 होता है जब इनपुट भिन्न होते हैं।',
      },
    },
  ],
  next: {
    slug: 'communication-systems',
    title: 'Communication Systems',
  },
};

export default chapter;
